import axios, { AxiosError } from 'axios';
import { AIAgentErrorCategory, AIAgentMappedError } from './AIAgentTypes';

const INTERNAL_ERROR_MESSAGE = 'Erro interno do agente';
const RECOVERABLE_MESSAGE =
  'Houve uma falha de comunicação com o Assistente SGD.\nTente novamente em alguns instantes.';
const INVALID_MESSAGE =
  'A mensagem enviada não é válida. Revise o conteúdo e tente novamente.';
const INVALID_USER_MESSAGE =
  'Não foi possível identificar sua conta para o assistente. Entre em contato com o suporte se o problema persistir.';
const LOCAL_UNAUTHORIZED_MESSAGE =
  'Entre novamente no SGD para continuar.';
const CONVERSATION_LIMIT_MESSAGE =
  'O limite de mensagens desta conversa foi atingido. Inicie uma nova conversa para continuar.';
const RATE_LIMITED_MESSAGE =
  'O agente atingiu o limite temporário. Aguarde para tentar novamente.';
const SESSION_INVALID_MESSAGE =
  'A sessão desta conversa não é mais válida. Inicie uma nova conversa para continuar.';
const TIMEOUT_MESSAGE =
  'O agente demorou mais que o esperado. Tente novamente manualmente.';
const RESET_FAILED_MESSAGE =
  'Não foi possível iniciar uma nova conversa. Tente novamente em instantes.';

interface IntegrationErrorBody {
  code?: string;
  message?: string;
  recoverable?: boolean;
  traceId?: string;
  retryAfterSeconds?: number;
  error?: string;
}

function isCanceled(error: unknown): boolean {
  return axios.isCancel(error);
}

function isTimeout(error: AxiosError): boolean {
  return (
    error.code === 'ECONNABORTED' ||
    error.message.toLowerCase().includes('timeout')
  );
}

function isNetworkError(error: AxiosError): boolean {
  return !error.response && error.message !== undefined;
}

function readRetryAfterSeconds(
  error: AxiosError<IntegrationErrorBody>
): number | undefined {
  const fromBody = error.response?.data?.retryAfterSeconds;
  if (typeof fromBody === 'number' && fromBody >= 1) {
    return Math.floor(fromBody);
  }

  const header = error.response?.headers?.['retry-after'];
  if (header == null) {
    return undefined;
  }

  const parsed = Number.parseInt(String(header), 10);
  if (Number.isFinite(parsed) && parsed >= 1) {
    return parsed;
  }

  return undefined;
}

function buildRateLimitedMessage(retryAfterSeconds?: number): string {
  if (retryAfterSeconds != null) {
    return `${RATE_LIMITED_MESSAGE} Aguarde cerca de ${retryAfterSeconds}s.`;
  }
  return RATE_LIMITED_MESSAGE;
}

function mapped(
  partial: Omit<AIAgentMappedError, 'isSessionInvalid'> & {
    isSessionInvalid?: boolean;
  }
): AIAgentMappedError {
  return {
    isSessionInvalid: false,
    ...partial,
  };
}

function mapByIntegrationCode(
  code: string | undefined,
  retryAfterSeconds?: number
): AIAgentMappedError | null {
  switch (code) {
    case 'INVALID_REQUEST':
      return mapped({
        kind: 'invalid-message',
        category: 'INVALID_MESSAGE',
        message: INVALID_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    case 'UNAUTHORIZED':
      return mapped({
        kind: 'invalid-user',
        category: 'LOCAL_UNAUTHORIZED',
        message: LOCAL_UNAUTHORIZED_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    case 'IDENTITY_UNAVAILABLE':
    case 'IDENTITY_REJECTED':
      return mapped({
        kind: 'invalid-user',
        category: 'IDENTITY_REJECTED',
        message: INVALID_USER_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    case 'SESSION_INVALID':
      return mapped({
        kind: 'session-invalid',
        category: 'SESSION_INVALID',
        message: SESSION_INVALID_MESSAGE,
        action: 'start-new-conversation',
        retryable: false,
        isConversationLimit: false,
        isSessionInvalid: true,
      });
    case 'RATE_LIMITED':
      return mapped({
        kind: 'rate-limited',
        category: 'RATE_LIMITED',
        message: buildRateLimitedMessage(retryAfterSeconds),
        action: 'retry',
        retryable: true,
        isConversationLimit: false,
        retryAfterSeconds,
      });
    case 'UPSTREAM_UNAVAILABLE':
      return mapped({
        kind: 'unavailable',
        category: 'UPSTREAM_UNAVAILABLE',
        message: RECOVERABLE_MESSAGE,
        action: 'retry',
        retryable: true,
        isConversationLimit: false,
      });
    case 'UPSTREAM_TIMEOUT':
      return mapped({
        kind: 'unavailable',
        category: 'UPSTREAM_TIMEOUT',
        message: TIMEOUT_MESSAGE,
        action: 'retry',
        retryable: true,
        isConversationLimit: false,
      });
    case 'INVALID_UPSTREAM_RESPONSE':
      return mapped({
        kind: 'internal',
        category: 'INVALID_RESPONSE',
        message: INTERNAL_ERROR_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    default:
      return null;
  }
}

export function mapAIAgentError(error: unknown): AIAgentMappedError | null {
  if (isCanceled(error)) {
    return null;
  }

  if (!axios.isAxiosError(error)) {
    return mapped({
      kind: 'internal',
      category: 'INTERNAL_ERROR',
      message: INTERNAL_ERROR_MESSAGE,
      action: 'none',
      retryable: false,
      isConversationLimit: false,
    });
  }

  const axiosError = error as AxiosError<IntegrationErrorBody>;

  if (isTimeout(axiosError)) {
    return mapped({
      kind: 'unavailable',
      category: 'UPSTREAM_TIMEOUT',
      message: TIMEOUT_MESSAGE,
      action: 'retry',
      retryable: true,
      isConversationLimit: false,
    });
  }

  if (isNetworkError(axiosError)) {
    return mapped({
      kind: 'unavailable',
      category: 'UPSTREAM_UNAVAILABLE',
      message: RECOVERABLE_MESSAGE,
      action: 'retry',
      retryable: true,
      isConversationLimit: false,
    });
  }

  const status = axiosError.response?.status;
  const bodyCode = axiosError.response?.data?.code;
  const retryAfterSeconds = readRetryAfterSeconds(axiosError);
  const fromCode = mapByIntegrationCode(bodyCode, retryAfterSeconds);
  if (fromCode) {
    return fromCode;
  }

  switch (status) {
    case 400:
      return mapped({
        kind: 'invalid-message',
        category: 'INVALID_MESSAGE',
        message: INVALID_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    case 401:
      return mapped({
        kind: 'invalid-user',
        category: 'LOCAL_UNAUTHORIZED',
        message: LOCAL_UNAUTHORIZED_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    case 403:
      return mapped({
        kind: 'invalid-user',
        category: 'IDENTITY_REJECTED',
        message: INVALID_USER_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    case 429:
      return mapped({
        kind: 'rate-limited',
        category: 'RATE_LIMITED',
        message: buildRateLimitedMessage(retryAfterSeconds),
        action: 'retry',
        retryable: true,
        isConversationLimit: false,
        retryAfterSeconds,
      });
    case 502:
    case 503:
      return mapped({
        kind: 'unavailable',
        category: 'UPSTREAM_UNAVAILABLE',
        message: RECOVERABLE_MESSAGE,
        action: 'retry',
        retryable: true,
        isConversationLimit: false,
      });
    case 504:
      return mapped({
        kind: 'unavailable',
        category: 'UPSTREAM_TIMEOUT',
        message: TIMEOUT_MESSAGE,
        action: 'retry',
        retryable: true,
        isConversationLimit: false,
      });
    case 500:
      return mapped({
        kind: 'internal',
        category: 'INTERNAL_ERROR',
        message: INTERNAL_ERROR_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
    default:
      return mapped({
        kind: 'internal',
        category: 'INTERNAL_ERROR',
        message: INTERNAL_ERROR_MESSAGE,
        action: 'none',
        retryable: false,
        isConversationLimit: false,
      });
  }
}

export function mapMalformedSuccessResponse(): AIAgentMappedError {
  return mapped({
    kind: 'internal',
    category: 'INVALID_RESPONSE',
    message: INTERNAL_ERROR_MESSAGE,
    action: 'none',
    retryable: false,
    isConversationLimit: false,
  });
}

export function mapConfigurationError(): AIAgentMappedError {
  return mapped({
    kind: 'configuration',
    category: 'INTERNAL_ERROR',
    message: 'O assistente não está disponível no momento.',
    action: 'none',
    retryable: false,
    isConversationLimit: false,
  });
}

export function mapInvalidIdentityError(): AIAgentMappedError {
  return mapped({
    kind: 'invalid-user',
    category: 'IDENTITY_REJECTED',
    message: INVALID_USER_MESSAGE,
    action: 'none',
    retryable: false,
    isConversationLimit: false,
  });
}

export function mapResetFailedError(): AIAgentMappedError {
  return mapped({
    kind: 'reset-failed',
    category: 'INTERNAL_ERROR',
    message: RESET_FAILED_MESSAGE,
    action: 'none',
    retryable: false,
    isConversationLimit: false,
  });
}

export function mapSessionInvalidError(): AIAgentMappedError {
  return mapped({
    kind: 'session-invalid',
    category: 'SESSION_INVALID',
    message: SESSION_INVALID_MESSAGE,
    action: 'start-new-conversation',
    retryable: false,
    isConversationLimit: false,
    isSessionInvalid: true,
  });
}

export {
  INTERNAL_ERROR_MESSAGE,
  RECOVERABLE_MESSAGE,
  INVALID_MESSAGE,
  INVALID_USER_MESSAGE,
  LOCAL_UNAUTHORIZED_MESSAGE,
  CONVERSATION_LIMIT_MESSAGE,
  RATE_LIMITED_MESSAGE,
  SESSION_INVALID_MESSAGE,
  TIMEOUT_MESSAGE,
  RESET_FAILED_MESSAGE,
};

export type { AIAgentErrorCategory };
